import React, { useState } from 'react';
import {
  Card, CardContent, Typography, Chip, Box, Stack, Button, Divider,
  CircularProgress, Tooltip, Collapse, IconButton,
} from '@mui/material';
import {
  AccessTime as TimeIcon,
  LocationOn as LocationIcon,
  CleaningServices as ServiceIcon,
  Place as DistanceIcon,
  AddTask as ClaimIcon,
  ChecklistRtl as ChecklistIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import axios from 'axios';
import PropertyInfoDisplay from './PropertyInfoDisplay';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const formatTime = (iso) => {
  if (!iso) return null;
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatDate = (iso) => {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
};

const formatPrice = (cents) => {
  if (cents == null) return null;
  return `$${(cents / 100).toFixed(2)}`;
};

/**
 * AvailableJobCard — single unclaimed job in the maid's "Available" list.
 *
 * Layout, top → bottom:
 *   1. Hero property photo + chips (PropertyInfoDisplay, hero variant)
 *   2. Header row: customer + distance chip
 *   3. Meta: time, address, service
 *   4. Estimated price (prominent)
 *   5. Expandable "View N tasks" — lazy-fetched, READ-ONLY
 *      (Maids can't edit the task list. Admin-side editor lives elsewhere.)
 *   6. Claim button
 */
const AvailableJobCard = ({ job, onClaim }) => {
  const [claiming, setClaiming] = useState(false);
  const [error, setError] = useState('');

  // Tasks lazy-load on first expand
  const [tasksOpen, setTasksOpen] = useState(false);
  const [tasks, setTasks] = useState(null);          // null = not loaded; [] = loaded empty
  const [tasksLoading, setTasksLoading] = useState(false);
  const [tasksError, setTasksError] = useState('');

  const customerName = job.customer?.customerName || job.customer?.name || 'New customer';
  const address =
    job.customer?.customerAddress ||
    (job.address
      ? [job.address.street, job.address.city, job.address.state, job.address.zip].filter(Boolean).join(', ')
      : null);

  const dateLabel = formatDate(job.utcScheduledDate);
  const timeLabel = formatTime(job.utcScheduledDate);
  const serviceLabel = (job.serviceType || job.jobType || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
  const priceLabel = formatPrice(job.totalAmount);
  const taskCount = job.totalTasks ?? 0;

  // ─── Handlers ──────────────────────────────────────────────────────────────
  const fetchTasks = async () => {
    if (tasks !== null) return; // already loaded
    setTasksLoading(true);
    setTasksError('');
    try {
      const res = await axios.get(`${API_URL}/api/jobs/${job.id}/tasks`, { headers: authHeaders() });
      setTasks(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setTasksError(err?.response?.data?.error || 'Could not load tasks.');
      setTasks([]);
    } finally {
      setTasksLoading(false);
    }
  };

  const handleToggleTasks = () => {
    if (!tasksOpen) fetchTasks();
    setTasksOpen((v) => !v);
  };

  const handleClaim = async () => {
    setError('');
    setClaiming(true);
    try {
      await onClaim(job.id);
      // Parent removes card from list on success
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Could not claim this job.');
      setClaiming(false);
    }
  };

  // Group fetched tasks by category for the read-only render
  const groupedTasks = (tasks || []).reduce((acc, t) => {
    const cat = t.category || 'General';
    (acc[cat] = acc[cat] || []).push(t);
    return acc;
  }, {});

  return (
    <Card variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
      {/* 1. Hero property photo + chips */}
      <PropertyInfoDisplay propertyInfo={job.propertyInfo} photo="hero" />

      <CardContent sx={{ pb: 1.5 }}>
        {/* 2. Header row */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1} mb={1}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ lineHeight: 1.3 }}>
            {customerName}
          </Typography>
          {job.distanceMiles != null && (
            <Tooltip title="Distance from your home address">
              <Chip
                icon={<DistanceIcon sx={{ fontSize: 14 }} />}
                label={`${job.distanceMiles} mi`}
                color="primary"
                size="small"
                variant="outlined"
              />
            </Tooltip>
          )}
        </Box>

        {/* 3. Meta rows */}
        <Stack spacing={0.5}>
          {(dateLabel || timeLabel) && (
            <Box display="flex" alignItems="center" gap={0.5}>
              <TimeIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {[dateLabel, timeLabel].filter(Boolean).join(' · ')}
              </Typography>
            </Box>
          )}

          {address && (
            <Box display="flex" alignItems="flex-start" gap={0.5}>
              <LocationIcon sx={{ fontSize: 15, color: 'text.secondary', mt: '2px' }} />
              <Typography variant="body2" color="text.secondary">{address}</Typography>
            </Box>
          )}

          {serviceLabel && (
            <Box display="flex" alignItems="center" gap={0.5}>
              <ServiceIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">{serviceLabel}</Typography>
            </Box>
          )}
        </Stack>

        {/* 4. Estimated price */}
        {priceLabel && (
          <Box display="flex" alignItems="center" gap={0.5} mt={1.5}>
            <MoneyIcon sx={{ fontSize: 18, color: 'success.main' }} />
            <Typography variant="subtitle1" fontWeight={700} color="success.main">
              {priceLabel}
            </Typography>
            <Typography variant="caption" color="text.secondary">estimated</Typography>
          </Box>
        )}

        {error && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
            {error}
          </Typography>
        )}
      </CardContent>

      {/* 5. Expandable read-only tasks */}
      {taskCount > 0 && (
        <>
          <Divider />
          <Box
            onClick={handleToggleTasks}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1,
              cursor: 'pointer',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <Box display="flex" alignItems="center" gap={0.5}>
              <ChecklistIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {tasksOpen ? 'Hide tasks' : `View tasks (${taskCount})`}
              </Typography>
            </Box>
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleToggleTasks(); }}>
              {tasksOpen ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
            </IconButton>
          </Box>
          <Collapse in={tasksOpen}>
            <Box px={2} pb={1.5}>
              {tasksLoading && (
                <Box display="flex" justifyContent="center" py={1}>
                  <CircularProgress size={20} />
                </Box>
              )}
              {!tasksLoading && tasksError && (
                <Typography variant="caption" color="error">{tasksError}</Typography>
              )}
              {!tasksLoading && !tasksError && tasks?.length === 0 && (
                <Typography variant="caption" color="text.secondary">No tasks for this job.</Typography>
              )}
              {!tasksLoading && !tasksError && Object.entries(groupedTasks).map(([cat, items]) => (
                <Box key={cat} mb={1}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary"
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
                    {cat}
                  </Typography>
                  <Box component="ul" sx={{ pl: 2.5, m: 0, mt: 0.25 }}>
                    {items.map((t) => (
                      <Box component="li" key={t.id} sx={{ pl: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{ color: t.isRequired ? 'text.primary' : 'text.secondary' }}
                        >
                          {t.title}
                          {t.isRequired && (
                            <Typography component="span" variant="caption" color="error.main" sx={{ ml: 0.5 }}>
                              *
                            </Typography>
                          )}
                          {t.estimatedMinutes && (
                            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                              · {t.estimatedMinutes} min
                            </Typography>
                          )}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          </Collapse>
        </>
      )}

      {/* 6. Claim */}
      <Divider />
      <Box px={2} py={1.5}>
        <Button
          fullWidth
          variant="contained"
          size="medium"
          startIcon={claiming ? <CircularProgress size={14} color="inherit" /> : <ClaimIcon />}
          onClick={handleClaim}
          disabled={claiming}
        >
          {claiming ? 'Claiming…' : 'Claim this job'}
        </Button>
      </Box>
    </Card>
  );
};

export default AvailableJobCard;
